import React from 'react'
import styled from 'styled-components'
import { Plus as IconPlus, Image as IconImage } from 'react-feather'
import SearchInput from './SearchInput'
import { FormattedMessage } from 'react-intl'
import { Show } from '../commons'
import { Spinner } from 'reactstrap'
import { queries } from '~/constants/mediaQuery'

export function ApprovalUsersAvatar({ approvalUsers }) {
  return (
    <AUAvatarContainer>
      {approvalUsers?.slice(0, 3).map((user, index) => (
        <div className="d-flex align-items-center" key={index}>
          <Avatar avatar={user.avatar} size={40} title={user.name} />
          {index === 2 && <IconPlus key={`icon-${index}`} />}
        </div>
      ))}
    </AUAvatarContainer>
  )
}

export function Avatar({ avatar, size, ...props }) {
  return (
    <AvatarContainer size={size} {...props}>
      {avatar ? <img src={avatar} alt="Avatar" /> : <IconImage height={60} />}
    </AvatarContainer>
  )
}

export function CustomTable({
  search,
  searchPlaceholder,
  setSearch,
  searchValue,
  headers,
  children,
  loading,
  id,
  searchSlot,
}) {
  return (
    <div className="custom-table" id={id}>
      {search && (
        <SearchInput
          placeholder={searchPlaceholder ?? 'Search'}
          onChange={e => setSearch(e.target.value.toLowerCase())}
          value={searchValue}
        />
      )}

      {searchSlot}

      <Show condition={loading}>
        <Spinner
          color="primary"
          size="lg"
          className="reload-spinner"
          style={{ position: 'absolute', left: '50%', top: '30rem', zIndex: 2 }}
        />
      </Show>
      <OverflowWrapper>
        <Table loading={loading?.toString()} searchSlot={searchSlot}>
          <thead>
            <tr>
              {headers?.map((header, index) =>
                header.name === '_blank' ? (
                  <th key={index} style={{ width: header.width }}>
                    {header.elem && <header.elem />}
                  </th>
                ) : (
                  <th
                    key={index}
                    style={{
                      width: header.width,
                      ...header.style,
                      textTransform: 'uppercase',
                    }}
                    className={header.center ? 'text-center' : ''}
                  >
                    <FormattedMessage id={header.name} />
                  </th>
                )
              )}
            </tr>
          </thead>
          <tbody>{children}</tbody>
        </Table>
      </OverflowWrapper>
    </div>
  )
}

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0 20px;
  overflow-x: auto;
  overflow-x: auto;
  @media ${queries.less.mds} {
    white-space: nowrap;
  }

  ${props =>
    props.loading === 'true' &&
    'user-select: none !important; pointer-events: none !important; opacity: 0.5;'}

  thead tr th {
    padding: 35px 0 10px;
    ${props =>
      props.searchSlot ? 'padding: 15px 0 10px;' : 'padding: 35px 0 10px;'}
    color: #161616;
    font-size: 16px;
    font-weight: 600;
  }
`

export const TableRow = styled.tr`
  background: #ffffff;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.15);
  border-radius: 8px;

  ${props =>
    props.needVerification
      ? `
    & td:before {
      content: '';
      background-color: #fe9f42;
      opacity: 0.4;
      z-index: 2;
      width: 100%;
      height: 100%;
      position: absolute;
      top: 0;
      left: 0;
      pointer-events: none;
    }

    & td:first-child:before {
      border-radius: 10px 0 0 10px;
    }

    & td:last-child:before {
      border-radius: 0 10px 10px 0;
    }`
      : ''};

  td {
    position: relative;
  }

  & td:first-child {
    border-radius: 10px 0 0 10px;
  }

  & td:last-child {
    border-radius: 0 10px 10px 0;
  }
`

export const OverflowWrapper = styled.div`
  overflow-x: auto;
  max-width: 100%;

  @media (max-width: 500px) {
    padding: 0 4px;
    margin: 0 -4px;
  }
`

export const Content = styled.td`
  padding: 20px 5px;
  margin-bottom: 0;
  font-weight: 600;
  font-size: 14px;
  line-height: 26px;
  color: #282828;
  text-align: center;

  &:first-child {
    padding-left: ${props => (props.select ? '1rem' : '40px')};
    padding-right: ${props => (props.select ? '1rem' : '0')};
  }

  &:last-child {
    padding-right: 40px;
  }

  ${props =>
    props.text === 'success'
      ? `font-weight: 600;
    font-size: 14px;
    line-height: 26px;
    color: #309e3a;
    margin-bottom: 0;`
      : props.text === 'big'
      ? `font-weight: 600;
    font-size: 14px;
    line-height: 21px;
    color: #161616;
    margin-bottom: 0;
    min-width: 30%;
    padding-left: 47px;
    text-align: left;`
      : props.text === 'danger'
      ? `font-weight: 600;
    font-size: 14px;
    line-height: 26px;
    color: #cf0000;
    margin-bottom: 0;`
      : props.text === 'warning'
      ? `font-weight: 600;
    font-size: 14px;
    line-height: 26px;
    color: #856404;
    margin-bottom: 0;`
      : ''}

  .user-info {
    color: #161616;
    text-align: left;
    h6 {
      font-family: 'Montserrat';
      font-weight: 600;
      font-size: 14px;
      line-height: 21px;
      margin-bottom: 0;
      text-align: left;
    }

    span {
      font-family: 'Montserrat';
      font-size: 14px;
      line-height: 21px;
      font-weight: normal;
      text-align: left;
    }
  }

  a {
    text-decoration: none;
    background-color: #efefef;
    border-radius: 8px;
    color: #606060;
    font-size: 14px;
    font-weight: 600;
    padding: 8px 18px;

    & + a {
      margin-left: 10px !important;
    }
  }
`

const AvatarContainer = styled.div`
  background-color: ${props => (props.avatar ? '#fff' : '#efefef')};
  min-width: ${props => (props.size ? props.size + 'px' : '56px')};
  max-width: ${props => (props.size ? props.size + 'px' : '56px')};
  height: ${props => (props.size ? props.size + 'px' : '56px')};
  width: 100%;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 30px;

  img {
    max-width: 100%;
  }
`

const AUAvatarContainer = styled.div`
  max-width: 200px;
  display: flex;
  align-items: center;

  > div {
    div {
      margin-right: 10px;
      svg {
        margin-left: 1px;
        width: 18px;
        height: 18px;
      }
    }
  }

  svg {
    stroke-width: 3;
    margin-left: -5px;
  }
`
